import React from "react";
import { useTranslation } from 'react-i18next';
import "./portfolio.css";
import Menu from "./Menu";
import { useNavigate } from "react-router-dom";

const Portfolio = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const visibleItems = React.useMemo(() => Menu.filter((curElem) => !curElem.hideInEverything), []);
    const categories = React.useMemo(() => [...new Set(visibleItems.map((item) => item.category))], [visibleItems]);
    const [activeFilter, setActiveFilter] = React.useState('Everything');
    const [clickedCardId, setClickedCardId] = React.useState(null);
    const [isNavigating, setIsNavigating] = React.useState(false);

  const categoryLabel = (category) => {
    switch (category) {
      case 'Web & Apps':
        return 'Web & Apps';
      case 'DevOps & Automatisering':
        return t('devops_and_automation') && !t('devops_and_automation').includes('_')
          ? t('devops_and_automation')
          : 'DevOps & Automatisering';
      case 'Cyber Security':
        return 'Cyber Security';
      case 'Cloud & Enterprise':
        return 'Cloud & Enterprise';
      default:
        return category;
    }
  };

  const ITEMS_PER_PAGE = 6;
  const [currentPage, setCurrentPage] = React.useState(1);

  const handleFilterClick = (category) => {
    setActiveFilter(category);
    setCurrentPage(1);
  };

  const filteredItems = React.useMemo(() => {
    return visibleItems.filter((item) => {
      return activeFilter === "Everything" || item.category === activeFilter;
    });
  }, [visibleItems, activeFilter]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = React.useMemo(() => {
    return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredItems, startIndex]);

    return (
      <section className="work container section" id="work">
        <span id="portfolio" style={{ position: 'relative', top: '-100px', display: 'block', visibility: 'hidden' }}></span>
        <h2 className="section__title">
          {t('recent_works')}
        </h2>

        <div className="work_filters_wrapper">
          <div className="work_filters">
              <button 
                type="button"
                className={`work_item ${activeFilter === 'Everything' ? 'active' : ''}`} 
                onClick={() => handleFilterClick("Everything")}
              >
                {t('everything')}
              </button>
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  className={`work_item ${activeFilter === category ? 'active' : ''}`}
                  onClick={() => handleFilterClick(category)}
                >
                  {categoryLabel(category)}
                </button>
              ))}
          </div>
        </div>
  
        <div className="work__container grid">
          {paginatedItems.map((elem) => {
            const { id, image, title, category, fallbackImage } = elem;
            return (
              <div 
                className={`work__card ${clickedCardId === id ? 'work__card--clicked' : ''}`} 
                key={id}
                onClick={() => {
                  if (isNavigating) return;
                  setIsNavigating(true);
                  setClickedCardId(id);
                  setTimeout(() => {
                    navigate(`/work/${id}`);
                  }, 80);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/work/${id}`);
                  }
                }}
              >
                <div className={`work__thumbnail ${clickedCardId === id ? 'work__thumbnail--clicked' : ''}`}>
                  <img
                    src={image}
                    alt={title}
                    className="work__img"
                    onError={(event) => {
                      if (fallbackImage && event.currentTarget.src !== fallbackImage) {
                        event.currentTarget.src = fallbackImage;
                      }
                    }}
                  />
                </div>

                <div className="work__card-body">
                  <span className="work__category">{categoryLabel(category)}</span>
                  <div className="work__title-wrapper">
                    <h3 className="work__title">{t(title)}</h3>
                    <i className="fas fa-arrow-right work__arrow"></i>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ==================== PAGINATION (6 PER PAGINA) ==================== */}
        {totalPages > 1 && (
          <div className="work-pagination">
            <button
              type="button"
              className="work-pagination-btn work-pagination-prev"
              onClick={() => {
                setCurrentPage((prev) => Math.max(prev - 1, 1));
                document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' });
              }}
              disabled={currentPage === 1}
              aria-label="Vorige pagina"
            >
              <i className="fas fa-chevron-left"></i> Vorige
            </button>

            <div className="work-pagination-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`work-pagination-num ${currentPage === page ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage(page);
                    document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  aria-label={`Pagina ${page}`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="work-pagination-btn work-pagination-next"
              onClick={() => {
                setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' });
              }}
              disabled={currentPage === totalPages}
              aria-label="Volgende pagina"
            >
              Volgende <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}
      </section>
    );
  };
  
export default Portfolio;
